/**
 * @fileoverview Vue i18n configuration and setup
 * @module plugins/i18n
 *
 * Provides internationalization support for the application.
 * Supports dynamic locale loading and RTL languages.
 *
 * IMPLEMENTATION STATUS:
 * - [F] English (en.json) - Complete, source of truth
 * - [F] Spanish (es.json) - Complete
 * - [S] Additional locales (fr, de, zh, ar, he) - Scale tier, add when needed
 * - [S] RTL support - Infrastructure ready, activate when Arabic/Hebrew added
 *
 * To add a new locale:
 * 1. Create src/locales/{locale}.json based on en.json structure
 * 2. Locale will be auto-detected via lazy loading
 */
import { createI18n, type I18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import enMessages from '../locales/en.json'

/** Supported locales */
export const SUPPORTED_LOCALES = {
  en: { name: 'English', nativeName: 'English', dir: 'ltr' },
  es: { name: 'Spanish', nativeName: 'Espa\u00f1ol', dir: 'ltr' },
  fr: { name: 'French', nativeName: 'Fran\u00e7ais', dir: 'ltr' },
  de: { name: 'German', nativeName: 'Deutsch', dir: 'ltr' },
  zh: { name: 'Chinese', nativeName: '\u4e2d\u6587', dir: 'ltr' },
  ar: { name: 'Arabic', nativeName: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629', dir: 'rtl' },
  he: { name: 'Hebrew', nativeName: '\u05e2\u05d1\u05e8\u05d9\u05ea', dir: 'rtl' }
} as const

export type SupportedLocale = keyof typeof SUPPORTED_LOCALES

/** Default locale */
export const DEFAULT_LOCALE: SupportedLocale = 'en'

/** Fallback locale */
export const FALLBACK_LOCALE: SupportedLocale = 'en'

/** Storage key for locale preference */
const LOCALE_STORAGE_KEY = 'planthra-locale'

export type MessageSchema = typeof enMessages

/**
 * Reactive flag indicating the user's preferred locale has been loaded.
 * Used to gate rendering until i18n is ready, preventing race condition
 * where cached JS loads before async locale messages are initialized.
 */
export const localeReady = ref(false)

/**
 * Create and configure i18n instance.
 * Always starts with English locale; call initializeLocale() after to load preferred locale.
 */
export function createI18nInstance(): I18n<{ en: MessageSchema }, {}, {}, string, false> {
  const i18n = createI18n({
    legacy: false,
    locale: DEFAULT_LOCALE, // Always start with English
    fallbackLocale: FALLBACK_LOCALE,
    messages: {
      en: enMessages
    },
    missingWarn: import.meta.env.DEV,
    fallbackWarn: import.meta.env.DEV
  })

  return i18n
}

/**
 * Initialize the user's preferred locale before app mount.
 * Loads async locale messages and sets localeReady when complete.
 * Call this before mounting the Vue app.
 */
export async function initializeLocale(
  i18n: I18n<{ en: MessageSchema }, {}, {}, string, false>
): Promise<void> {
  const savedLocale = typeof localStorage !== 'undefined'
    ? localStorage.getItem(LOCALE_STORAGE_KEY) as SupportedLocale | null
    : null

  const browserLocale = typeof navigator !== 'undefined'
    ? (navigator.language.split('-')[0] as SupportedLocale)
    : DEFAULT_LOCALE

  const targetLocale = savedLocale
    || (SUPPORTED_LOCALES[browserLocale] ? browserLocale : DEFAULT_LOCALE)

  if (targetLocale !== 'en') {
    await loadLocaleMessages(i18n, targetLocale)
    ;(i18n.global.locale.value as string) = targetLocale

    // Update document attributes
    if (typeof document !== 'undefined') {
      document.documentElement.lang = targetLocale
      document.documentElement.dir = SUPPORTED_LOCALES[targetLocale].dir
    }
  }

  localeReady.value = true
}

/** Lazy-loaded locale messages cache */
const loadedLocales = new Set<string>(['en'])

/**
 * Load locale messages dynamically
 */
export async function loadLocaleMessages(
  i18n: I18n<{ en: MessageSchema }, {}, {}, string, false>,
  locale: SupportedLocale
): Promise<void> {
  if (loadedLocales.has(locale)) {
    return
  }

  try {
    // Dynamic import of locale messages
    const messages = await import(`../locales/${locale}.json`)
    i18n.global.setLocaleMessage(locale, messages.default)
    loadedLocales.add(locale)
  } catch (error) {
    console.warn(`Failed to load locale: ${locale}`, error)
    // Fall back to English
    if (locale !== FALLBACK_LOCALE) {
      await loadLocaleMessages(i18n, FALLBACK_LOCALE)
    }
  }
}

/**
 * Set the active locale
 */
export async function setLocale(
  i18n: I18n<{ en: MessageSchema }, {}, {}, string, false>,
  locale: SupportedLocale
): Promise<void> {
  // Load messages if not already loaded
  await loadLocaleMessages(i18n, locale)

  // Set locale
  ;(i18n.global.locale.value as string) = locale

  // Persist preference
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  }

  // Update document attributes
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale
    document.documentElement.dir = SUPPORTED_LOCALES[locale].dir
  }
}

/**
 * Composable for locale management
 */
export function useLocale(i18n: I18n<{ en: MessageSchema }, {}, {}, string, false>) {
  const currentLocale = computed(() => i18n.global.locale.value as SupportedLocale)
  const isRTL = computed(() => SUPPORTED_LOCALES[currentLocale.value]?.dir === 'rtl')
  const availableLocales = computed(() =>
    Object.entries(SUPPORTED_LOCALES).map(([code, info]) => ({
      code: code as SupportedLocale,
      ...info
    }))
  )

  const changeLocale = async (locale: SupportedLocale) => {
    await setLocale(i18n, locale)
  }

  return {
    currentLocale,
    isRTL,
    availableLocales,
    changeLocale,
    t: i18n.global.t
  }
}

/**
 * Get direction for a locale
 */
export function getLocaleDirection(locale: SupportedLocale): 'ltr' | 'rtl' {
  return SUPPORTED_LOCALES[locale]?.dir ?? 'ltr'
}

/**
 * Check if a locale is RTL
 */
export function isRTLLocale(locale: SupportedLocale): boolean {
  return getLocaleDirection(locale) === 'rtl'
}
